// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Link } from "react-router-dom";

import { IState } from "states/states";
import { IAction, ActionType, raiseAction } from "actions";
import { IActionTypeSetPeriod, ActionGraphCommand, IActionChangeDashboardWidget } from "actions";
import { DashboardPanel, IDashboardEntry, MainPanel, TimePeriod } from "types";
import { isSet } from "shared/utils/utils";
import { toDropdownItems, getActiveDashboardItems } from "guiutils";

import Dropdown from "view/comp/Dropdown";
import EnergyGraph from "view/comp/EnergyGraph";
import IoList from "view/page/IoList";
import MonitorGraph from "view/comp/MonitorGraph";
import Alarm from "./Alarm";
import Reports from "./Reports";
import TariffCircuitList from "view/comp/TariffCircuitList";
import ControlCircuitSelect from "view/comp/ControlCircuitSelect";
import TrendMonitor from "./TrendMonitor";
import TrendMonitorSelect from "view/comp/TrendMonitorSelect";
import EMobilitySelect from "view/comp/EMobilitySelect";
import EMobilityWidget from "view/comp/EMobilityWidget";
import SiteWidget from "view/comp/SiteWidget";

import IconFullscreen from "icons/iconFullscreen";

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface IDashboardProps {
  timePeriod: TimePeriod;
  showAlarms: boolean;
  showOcppChargers: boolean;
  showInputs: boolean;
  showOutputs: boolean;
  dashboardItems: (DashboardPanel | undefined)[];
  availableDashboards: IDashboardEntry[];
}

const mapStateToProps = (state: IState) => {
  const { dashboards, activeDashboardItems } = getActiveDashboardItems(state);

  const inputCount =
    state.para.parameter?.input?.filter((input) => {
      return input.webActive;
    }).length ?? 0;

  const outputCount =
    state.para.parameter?.output?.filter((output) => {
      return output.webActive;
    }).length ?? 0;

  const props: IDashboardProps = {
    timePeriod: state.gui.trendPeriod.timePeriod,
    showAlarms: (state.data?.alarms.length ?? 0) > 0,
    showOcppChargers: IS_CLOUD && state.ocpp.charger.length > 0,
    showInputs: inputCount > 0,
    showOutputs: outputCount > 0,
    dashboardItems: activeDashboardItems,
    availableDashboards: dashboards,
  };
  return props;
};

interface IDashboardDispatch {
  onPeriodChanged: (value: TimePeriod) => void;
  onDropdownSelectionChanged: (index: string, value: DashboardPanel | undefined) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: IDashboardDispatch = {
    onPeriodChanged: (value: TimePeriod) => {
      const payload: IActionTypeSetPeriod = {
        identifier: "trendPeriod",
        command: ActionGraphCommand.SET_PERIOD,
        timePeriod: value,
        selectedDate: Date.now() / 1000,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_PERIOD, payload));
      // load new graph data
      dispatch(raiseAction(ActionType.ACT_UPDATE_TREND));
    },
    onDropdownSelectionChanged: (index: string, value: DashboardPanel | undefined) => {
      const payload: IActionChangeDashboardWidget = {
        index: Number.parseInt(index),
        panel: value,
      };
      dispatch(raiseAction(ActionType.ACT_SELECT_DASHBOARD_WIDGET, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msg_period_caption: {
    id: "Select period",
  },
  msg_dashboard_items: {
    id: "Dashboard-Items",
  },
  msg_no_dashboard_item: {
    id: "Select Dashboard",
  },
});

type IDashboardWidgets = {
  [panel in DashboardPanel]: (widgetNr: number) => JSX.Element;
};

class DashboardComponent extends React.Component<IDashboardProps & IDashboardDispatch> {
  static contextType = IntlContext;
  declare context: IntlShape;

  private dashboarWidgets: IDashboardWidgets = {
    [DashboardPanel.EMPTY]: this.renderNoWidgetAvailable.bind(this),
    [DashboardPanel.ALARMS]: this.renderAlarmsWidget.bind(this),
    [DashboardPanel.EMOBILTY]: this.renderChargersWidget.bind(this),
    [DashboardPanel.ENERGY]: this.renderEnergyWidget.bind(this),
    [DashboardPanel.INPUTS]: this.renderInputListWidget.bind(this),
    [DashboardPanel.MONITOR]: this.renderMonitoringWidget.bind(this),
    [DashboardPanel.OUTPUTS]: this.renderOutputListWidget.bind(this),
    [DashboardPanel.POWER]: this.renderPowerWidget.bind(this),
    [DashboardPanel.REPORTS]: this.renderReportsWidget.bind(this),
    [DashboardPanel.SITEINFOS]: this.renderSiteInfosWidget.bind(this),
    [DashboardPanel.TRENDMONITOR]: this.renderTrendMonitorWidget.bind(this),
  };

  public render() {
    return (
      <div id="dashboard" className="row">
        <div className="dashboardpanel col-md-6 col-xxl-4">{this.renderDashboardWidget(0)}</div>
        <div className="dashboardpanel col-md-6 col-xxl-4">{this.renderDashboardWidget(1)}</div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-md-block">
          {this.renderDashboardWidget(2)}
        </div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-md-block">
          {this.renderDashboardWidget(3)}
        </div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-xxl-block">
          {this.renderDashboardWidget(4)}
        </div>
        <div className="dashboardpanel col-md-6 col-xxl-4 d-none d-xxl-block">
          {this.renderDashboardWidget(5)}
        </div>
      </div>
    );
  }

  private renderDashboardWidget(widgetIndex: number) {
    const widgetRenderer =
      this.props.dashboardItems[widgetIndex] ?
        this.dashboarWidgets[this.props.dashboardItems[widgetIndex]!]
      : undefined;
    if (isSet(widgetRenderer)) {
      return widgetRenderer(widgetIndex);
    } else {
      return this.renderNoWidgetAvailable(widgetIndex);
    }
  }

  private panelName(panel: DashboardPanel | undefined) {
    // ignoreTag: names like "<automatic>" are plain text, not rich text tags
    return this.context.formatMessage({ id: `DashboardPanel_${panel ?? ""}` }, undefined, {
      ignoreTag: true,
    });
  }

  private renderWidgetSelector(widgetNr: number) {
    let allOptions: (DashboardPanel | undefined)[] = Object.keys(
      this.dashboarWidgets,
    ) as DashboardPanel[];
    allOptions = allOptions
      .concat(
        undefined, // auto-select
      )
      .filter(
        (item) =>
          !isSet(item) ||
          item === DashboardPanel.EMPTY ||
          this.props.availableDashboards.some((availableItems) => availableItems.entry === item),
      )
      .sort((a, b) =>
        b === DashboardPanel.EMPTY ?
          -1 // empty selection shall be last
        : this.panelName(a).localeCompare(this.panelName(b)),
      );
    const items = toDropdownItems(
      allOptions,
      `${widgetNr}`,
      this.props.onDropdownSelectionChanged,
      (value) => this.panelName(value),
    );

    return (
      <Dropdown
        className="me-1"
        items={items}
        caption={this.context.formatMessage(messages.msg_dashboard_items)}
        curItem={
          this.props.dashboardItems[widgetNr] ?
            this.context.formatMessage({
              id: `DashboardPanel_${this.props.dashboardItems[widgetNr]}`,
            })
          : this.context.formatMessage(messages.msg_no_dashboard_item)
        }
      />
    );
  }

  private renderDashboardHeader(
    widgetNr: number,
    shouldRenderFSIcon: boolean,
    panel: MainPanel,
    renderOptionalSelector?: () => JSX.Element,
  ) {
    return (
      <div className="header">
        {this.renderWidgetSelector(widgetNr)}
        <div id="trendheading"></div>
        {isSet(renderOptionalSelector) && renderOptionalSelector()}
        {shouldRenderFSIcon && (
          <Link to={`../${panel}?widget=${widgetNr}`} className="icon-fullscreen">
            <IconFullscreen />
          </Link>
        )}
      </div>
    );
  }

  private renderPowerWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.POWER)}
        <div className="widgetdatapanel">
          <TariffCircuitList isDashboard={true} panel={DashboardPanel.POWER} />
        </div>
      </div>
    );
  }

  private renderReportsWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.REPORT)}
        <div className="widgetdatapanel">
          <Reports isDashboard={true} />
        </div>
      </div>
    );
  }

  private renderSiteInfosWidget(widgetNr: number) {
    return (
      <div style={{ height: "100%" }}>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.LOCAL_SETTINGS)}
        <div className="widgetdatapanel">
          <SiteWidget />
        </div>
      </div>
    );
  }

  private renderTrendMonitorWidget(widgetNr: number) {
    return (
      <div id="dashboardmonitor" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(widgetNr, true, MainPanel.TRENDMONITOR, () => (
          <TrendMonitorSelect />
        ))}
        <div className="widgetdatapanel flex-self flex-content graph-shrink">
          <TrendMonitor isDashboard={true} />
        </div>
      </div>
    );
  }

  private renderInputListWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, this.props.showInputs, MainPanel.IOS)}
        <div className="widgetdatapanel">
          <IoList
            isInputEditable={false}
            isOutputEditable={false}
            isDashboard={true}
            onlyType="input"
          />
        </div>
      </div>
    );
  }
  private renderOutputListWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, this.props.showOutputs, MainPanel.IOS)}
        <div className="widgetdatapanel">
          <IoList
            isInputEditable={false}
            isOutputEditable={false}
            isDashboard={true}
            onlyType="output"
          />
        </div>
      </div>
    );
  }

  private renderAlarmsWidget(widgetNr: number) {
    return (
      <div>
        {this.renderDashboardHeader(widgetNr, true, MainPanel.ALARMS)}
        <div className="widgetdatapanel">
          <Alarm isDashboard={true} />
        </div>
      </div>
    );
  }

  private renderChargersWidget(widgetNr: number) {
    return (
      <div id="dashboardchargers" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(
          widgetNr,
          this.props.showOcppChargers,
          MainPanel.CHARGERLIST,
          () => (
            <EMobilitySelect />
          ),
        )}
        <div className="widgetdatapanel flex-self flex-content graph-shrink">
          <EMobilityWidget />
        </div>
      </div>
    );
  }

  private renderEnergyWidget(widgetNr: number) {
    const periodOptions = [TimePeriod.DAY, TimePeriod.WEEK, TimePeriod.MONTH, TimePeriod.YEAR];
    const periodItems = toDropdownItems(periodOptions, "thisperiod", (_name, value) =>
      this.props.onPeriodChanged(value as any as TimePeriod),
    );

    return (
      <div id="dashboardenergy" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(widgetNr, true, MainPanel.ENERGY, () => (
          <>
            {IS_CLOUD && (
              <div id="periodselect" className="d-none d-sm-block d-md-none d-lg-block">
                <Dropdown
                  items={periodItems}
                  caption={this.context.formatMessage(messages.msg_period_caption)}
                  curItem={this.context.formatMessage({
                    id: `thisperiod_${this.props.timePeriod ?? ""}`,
                  })}
                />
              </div>
            )}
          </>
        ))}
        <div className="widgetdatapanel flex-self flex-content graph-shrink">
          <EnergyGraph showLegend={false} panel={DashboardPanel.ENERGY} isDashboard={true} />
        </div>
      </div>
    );
  }

  private renderMonitoringWidget(widgetNr: number) {
    return (
      <div id="dashboardmonitor" className="dashboardpanel flex-content">
        {this.renderDashboardHeader(widgetNr, true, MainPanel.MONITOR, () => (
          <ControlCircuitSelect isDashboard={true} />
        ))}
        <div className="widgetdatapanel flex-self flex-content graph-shrink">
          <MonitorGraph />
        </div>
      </div>
    );
  }

  private renderNoWidgetAvailable(widgetNr: number) {
    return (
      <div>
        <div className="header">{this.renderWidgetSelector(widgetNr)}</div>
        <div id="trendheading"></div>
        <div>
          <FormattedMessage id="No widget selected." />
        </div>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(DashboardComponent);
